import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash, PencilSimple, Signature } from "@phosphor-icons/react";
import { formatIdDateTime } from "@/lib/format";

export default function SignatureLibrary() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // signature obj or null (new)
  const [form, setForm] = useState({ name: "", image_data: "", aktif: true });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/signatures");
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", image_data: "", aktif: true });
    setOpen(true);
  };

  const openEdit = (sig) => {
    setEditing(sig);
    setForm({
      name: sig.name,
      image_data: sig.image_data ? "data:image/png;base64," + sig.image_data : "",
      aktif: sig.aktif,
    });
    setOpen(true);
  };

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    if (file.size > 1024 * 1024) {
      toast.error("Ukuran file maks 1MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image_data: reader.result }));
    reader.readAsDataURL(file);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Nama wajib diisi");
      return;
    }
    if (!editing && !form.image_data) {
      toast.error("Upload gambar tanda tangan");
      return;
    }
    setSubmitting(true);
    try {
      if (editing) {
        const payload = { name: form.name, aktif: form.aktif };
        if (form.image_data && form.image_data.startsWith("data:")) {
          payload.image_data = form.image_data;
        }
        await api.put(`/signatures/${editing.id}`, payload);
        toast.success("Tanda tangan diupdate");
      } else {
        await api.post("/signatures", form);
        toast.success("Tanda tangan disimpan");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onToggle = async (sig) => {
    try {
      await api.put(`/signatures/${sig.id}`, { aktif: !sig.aktif });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onDelete = async (sig) => {
    if (!window.confirm(`Hapus tanda tangan "${sig.name}"?`)) return;
    try {
      await api.delete(`/signatures/${sig.id}`);
      toast.success("Terhapus");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-6xl">
      <div className="flex flex-col md:flex-row md:items-end justify-between border-b border-border pb-6 mb-6 gap-4">
        <div>
          <div className="overline">Setelan</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2 flex items-center gap-3">
            <Signature size={30} weight="bold" /> Library Tanda Tangan
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Simpan tanda tangan siap-pakai — akan muncul sebagai pilihan saat buat SPB.
            Setiap admin punya library sendiri.
          </p>
        </div>
        <Button
          onClick={openNew}
          className="rounded-none font-bold uppercase tracking-wider"
          data-testid="add-signature-button"
        >
          <Plus size={16} className="mr-2" /> Upload Tanda Tangan
        </Button>
      </div>

      {loading ? (
        <div className="p-8 text-center overline">Memuat...</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-border p-12 text-center text-muted-foreground">
          Belum ada tanda tangan. Klik <strong>Upload Tanda Tangan</strong> untuk menambahkan.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((sig) => (
            <div
              key={sig.id}
              className={`border p-4 bg-card ${sig.aktif ? "border-border" : "border-border/40 opacity-60"}`}
              data-testid={`signature-card-${sig.id}`}
            >
              <div className="border border-border bg-white p-3 mb-3 h-32 flex items-center justify-center">
                {sig.image_data ? (
                  <img
                    src={`data:image/png;base64,${sig.image_data}`}
                    alt={sig.name}
                    className="max-h-full max-w-full object-contain"
                  />
                ) : (
                  <span className="text-muted-foreground text-xs">No preview</span>
                )}
              </div>
              <div className="flex items-center justify-between mb-2">
                <div className="font-bold truncate">{sig.name}</div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={sig.aktif}
                    onCheckedChange={() => onToggle(sig)}
                    data-testid={`toggle-signature-${sig.id}`}
                  />
                </div>
              </div>
              <div className="text-xs text-muted-foreground mb-3">
                {formatIdDateTime(sig.created_at)}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-none flex-1"
                  onClick={() => openEdit(sig)}
                  data-testid={`edit-signature-${sig.id}`}
                >
                  <PencilSimple size={14} className="mr-1" /> Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-none text-destructive"
                  onClick={() => onDelete(sig)}
                  data-testid={`delete-signature-${sig.id}`}
                >
                  <Trash size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Edit Tanda Tangan" : "Upload Tanda Tangan Baru"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="signature-form">
            <div>
              <Label className="overline block mb-2">Nama / Label *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                required
                placeholder="contoh: Direktur Pak Budi"
                className="rounded-none h-11"
                data-testid="input-signature-name"
              />
            </div>
            <div>
              <Label className="overline block mb-2">
                Gambar Tanda Tangan {editing ? "(kosongkan jika tidak diganti)" : "*"}
              </Label>
              <Input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={onFileChange}
                className="rounded-none"
                data-testid="input-signature-file"
              />
              {form.image_data && (
                <div className="mt-3 border border-border bg-white p-3">
                  <img src={form.image_data} alt="Preview" className="max-h-32" />
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={form.aktif}
                onCheckedChange={(v) => setForm((f) => ({ ...f, aktif: v }))}
                data-testid="input-signature-aktif"
              />
              <span className="text-sm">Aktif (tampil di pilihan saat buat SPB)</span>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                className="rounded-none"
                onClick={() => setOpen(false)}
              >
                Batal
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="rounded-none font-bold uppercase"
                data-testid="save-signature-button"
              >
                {submitting ? "Menyimpan..." : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
